/**
 * i18n Context Provider - V2 Page-Based
 * Lädt Translations basierend auf ENV Variable + Current Route
 */

import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCurrentLanguage } from '@/core/config/language-v2';
import { getNestedValue, interpolate } from './config';
import type { I18nContext } from './config';
import { loadPageContent } from './loaders';

const I18nContext = createContext<I18nContext | undefined>(undefined);

interface I18nProviderProps {
  children: React.ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const language = getCurrentLanguage();
  const location = useLocation();
  const [currentTranslations, setCurrentTranslations] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load content when route or language changes
  useEffect(() => {
    let cancelled = false;
    
    async function loadContent() {
      setIsLoading(true);
      try {
        const content = await loadPageContent(language, location.pathname);
        console.log('[I18nProvider] Loaded content for', { language, path: location.pathname, keys: Object.keys(content), hasLanding: 'landing' in content });
        if (!cancelled) {
          setCurrentTranslations(content);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    
    loadContent();
    
    return () => {
      cancelled = true;
    };
  }, [language, location.pathname]);

  const t: I18nContext['t'] = useMemo(() => {
    return (key: string, params?: Record<string, string | number>) => {
      const value = getNestedValue(currentTranslations, key);
      return interpolate(value, params);
    };
  }, [currentTranslations]);

  const value: I18nContext = useMemo(
    () => ({ language, t }),
    [language, t]
  );

  // Show loading state while content is being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContext {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}


